const cssCheatSheet = {
  id: "css",
  title: "CSS3 Cheatsheet",
  level: "Beginner to Advanced",
  description:
    "A comprehensive guide to CSS3 properties, selectors, and modern styling techniques",
  language: "css",
  category: "Web Development",
  sections: [
    {
      title: "Syntax & Selectors",
      icon: "🎯",
      items: [
        {
          title: "Basic Syntax",
          content: "CSS rule structure with selector, property, and value",
          code: `selector {
  property: value;
  /* Multiple declarations allowed */
  color: blue;
  font-size: 16px;
}`,
          note: "Each CSS rule consists of a selector and one or more declarations inside curly braces",
        },
        {
          title: "Element Selectors",
          content: "Select HTML elements by their tag name",
          code: `/* Basic element selector */
p { color: blue; }

/* Multiple elements */
h1, h2, h3 { font-family: Arial; }

/* Universal selector */
* { margin: 0; padding: 0; }`,
        },
        {
          title: "Class & ID Selectors",
          code: `/* Class selector */
.primary-button { background: blue; }

/* Multiple classes */
.btn.large { padding: 20px; }

/* ID selector */
#header { position: fixed; }`,
          note: "Classes are reusable and can be applied to multiple elements, while IDs must be unique",
        },
      ],
    },
    {
      title: "Layout & Box Model",
      icon: "📦",
      items: [
        {
          title: "Box Model Properties",
          content: "Control element dimensions and spacing",
          code: `div {
  width: 200px;
  height: 100px;
  padding: 20px;
  border: 1px solid #000;
  margin: 10px;
}

/* Box-sizing */
* {
  box-sizing: border-box;
}`,
          note: "box-sizing: border-box includes padding and border in element's total width/height",
        },
        {
          title: "Display Properties",
          code: `/* Common display values */
.element {
  display: block;
  display: inline;
  display: inline-block;
  display: flex;
  display: grid;
  display: none;
}`,
          note: "Controls how elements are rendered in the document flow",
        },
        {
          title: "Position Properties",
          code: `/* Positioning */
.element {
  position: relative;
  position: absolute;
  position: fixed;
  position: sticky;
  top: 0;
  left: 0;
  z-index: 100;
}`,
        },
      ],
    },
    {
      title: "Flexbox",
      icon: "📏",
      items: [
        {
          title: "Flex Container",
          content: "Properties for the flex container element",
          code: `/* Container properties */
.container {
  display: flex;
  flex-direction: row | column;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}`,
          note: "Flexbox makes it easier to create responsive layouts and align items",
        },
        {
          title: "Flex Items",
          code: `/* Item properties */
.item {
  flex: 1;
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: auto;
  align-self: center;
  order: 1;
}`,
        },
      ],
    },
    {
      title: "Grid",
      icon: "🔲",
      items: [
        {
          title: "Grid Container",
          content: "Define grid layout properties",
          code: `/* Grid container */
.grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: auto;
  gap: 20px;
  grid-template-areas: 
    "header header header"
    "sidebar main main"
    "footer footer footer";
}`,
        },
        {
          title: "Grid Items",
          code: `/* Grid item placement */
.item {
  grid-column: 1 / 3;
  grid-row: 1 / 2;
  grid-area: header;
}`,
          note: "Grid provides powerful 2D layout control",
        },
      ],
    },
    {
      title: "Typography",
      icon: "📏",
      items: [
        {
          title: "Font Properties",
          code: `/* Font styling */
.text {
  font-family: 'Arial', sans-serif;
  font-size: 16px;
  font-weight: bold;
  font-style: italic;
  line-height: 1.5;
  letter-spacing: 0.5px;
  text-align: center;
  text-transform: uppercase;
}`,
        },
        {
          title: "Text Effects",
          code: `/* Text decorations */
.text {
  text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
  text-decoration: underline;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}`,
        },
      ],
    },
    {
      title: "Colors & Backgrounds",
      icon: "📦",
      items: [
        {
          title: "Color Values",
          code: `/* Color formats */
.element {
  color: #ff0000;
  color: rgb(255, 0, 0);
  color: rgba(255, 0, 0, 0.5);
  color: hsl(0, 100%, 50%);
  color: hsla(0, 100%, 50%, 0.5);
}`,
        },
        {
          title: "Backgrounds",
          code: `/* Background properties */
.element {
  background-color: #f0f0f0;
  background-image: url('image.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background: linear-gradient(to right, #ff0000, #00ff00);
}`,
        },
      ],
    },
    {
      title: "Transitions & Animations",
      icon: "🎬",
      items: [
        {
          title: "Transitions",
          content: "Smooth property changes",
          code: `/* Basic transition */
.element {
  transition: all 0.3s ease;
  transition: transform 0.3s ease-in-out;
}

/* Multiple transitions */
.element {
  transition: 
    background-color 0.3s ease,
    transform 0.5s ease-in-out;
}`,
        },
        {
          title: "Animations",
          code: `/* Keyframe animation */
@keyframes slide-in {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(0); }
}

.element {
  animation: slide-in 0.5s ease-out;
  animation-fill-mode: forwards;
  animation-iteration-count: infinite;
}`,
        },
      ],
    },
    {
      title: "Responsive Design",
      icon: "📱",
      items: [
        {
          title: "Media Queries",
          content: "Adapt styles for different screen sizes",
          code: `/* Mobile first approach */
@media screen and (min-width: 768px) {
  .container {
    width: 750px;
  }
}

@media screen and (min-width: 1024px) {
  .container {
    width: 960px;
  }
}`,
          note: "Use breakpoints to create responsive layouts",
        },
        {
          title: "Viewport Units",
          code: `/* Viewport-relative units */
.hero {
  height: 100vh;
  width: 100vw;
  padding: 5vmin;
  font-size: calc(16px + 1vw);
}`,
        },
      ],
    },
    {
      title: "Transforms & Effects",
      icon: "✨",
      items: [
        {
          title: "Transforms",
          code: `/* 2D Transforms */
.element {
  transform: translate(50px, 100px);
  transform: rotate(45deg);
  transform: scale(1.5);
  transform: skew(10deg);
}

/* 3D Transforms */
.element {
  transform: rotateX(45deg);
  transform: perspective(1000px);
  transform-style: preserve-3d;
}`,
        },
        {
          title: "Filters & Effects",
          code: `/* Filters */
.element {
  filter: blur(5px);
  filter: brightness(150%);
  filter: contrast(200%);
  filter: drop-shadow(5px 5px 10px #000);
  backdrop-filter: blur(10px);
}`,
        },
      ],
    },
  ],
};

export default cssCheatSheet;
